import {
  type Firestore,
  increment,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { useCollection } from 'vuefire';
import { computed, type Ref } from 'vue';
import Repository from '@/service/repository';
import type { AuthIdentifier, Groups } from '@/service/types';
import type { GroceryPlan } from '@/service/groceries/planAdditions';
import {
  type Grocery,
  type GroceryChanges,
  type GroceryInput,
  GrocerySchema,
} from '@/service/groceries/types';

export interface GroceryClientInterface {
  collectionRef(): Ref<Grocery[]>;

  get(): Promise<Grocery[]>;

  add(item?: GroceryInput): Promise<Grocery>;

  update(id: string, changes: GroceryChanges): Promise<void>;

  remove(id: string): Promise<void>;

  removeAll(ids: string[]): Promise<void>;

  apply(plan: GroceryPlan): Promise<void>;

  revert(plan: GroceryPlan): Promise<void>;
}

// Firestore rejects undefined field values
function withoutUndefined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, field]) => field !== undefined),
  ) as Partial<T>;
}

export function byCreation(a: Grocery, b: Grocery): number {
  return a.createdAt - b.createdAt || a.id.localeCompare(b.id);
}

export default class GroceryClient implements GroceryClientInterface {
  private readonly timeout: number = 100;
  private readonly items: Repository<Grocery>;
  private readonly uid: string;

  // The identifier's groups are the family whose items are shown
  constructor(
    private readonly firestore: Firestore,
    identifier: AuthIdentifier,
  ) {
    this.items = new Repository<Grocery>('groceries', firestore, identifier);
    this.uid = identifier.uid;
  }

  static async setup(
    firestore: Firestore,
    identifier: AuthIdentifier,
  ): Promise<GroceryClient> {
    const groups = new Repository<Groups>('groups', firestore, identifier);
    const { users } = await groups.getOne(identifier.uid);
    return new GroceryClient(firestore, { ...identifier, groups: users });
  }

  collectionRef(): Ref<Grocery[]> {
    const items = useCollection(this.items.collectionRef(), {
      ssrKey: 'groceries',
    });
    return computed(() =>
      items.value
        // VueFire keeps the document id in a non-enumerable property
        .map((item) => GrocerySchema.parse({ ...item, id: item.id }))
        .sort(byCreation),
    );
  }

  async get(): Promise<Grocery[]> {
    const items = await this.items.get();
    return items.map((item) => GrocerySchema.parse(item)).sort(byCreation);
  }

  async add(item: GroceryInput = {}): Promise<Grocery> {
    const grocery = GrocerySchema.parse(item);
    await this.items.save(grocery);
    return grocery;
  }

  // Only the changed fields, so the item keeps its owner and concurrent
  // edits to other fields survive
  async update(id: string, changes: GroceryChanges): Promise<void> {
    await this.settle(updateDoc(this.items.documentRef(id), changes));
  }

  async remove(id: string): Promise<void> {
    await this.items.remove(id);
  }

  async removeAll(ids: string[]): Promise<void> {
    const batch = writeBatch(this.firestore);
    for (const id of ids) {
      batch.delete(this.items.documentRef(id));
    }
    await this.settle(batch.commit());
  }

  // In one batch; increment() keeps amounts right when family members add
  // to the same item at the same time
  async apply(plan: GroceryPlan): Promise<void> {
    await this.writePlan(plan, 1);
  }

  async revert(plan: GroceryPlan): Promise<void> {
    await this.writePlan(plan, -1);
  }

  private async writePlan(plan: GroceryPlan, direction: 1 | -1) {
    const batch = writeBatch(this.firestore);
    for (const item of plan.add) {
      const ref = this.items.documentRef(item.id);
      if (direction === 1) {
        batch.set(ref, { ...withoutUndefined(item), user: this.uid });
      } else {
        batch.delete(ref);
      }
    }
    for (const { id, by } of plan.increase) {
      batch.update(this.items.documentRef(id), {
        amount: increment(direction * by),
      });
    }
    await this.settle(batch.commit());
  }

  // Offline writes only resolve once synced; like Repository, don't wait
  // for that
  private async settle(write: Promise<void>): Promise<void> {
    await Promise.race([
      write,
      new Promise((resolve) => setTimeout(resolve, this.timeout)),
    ]);
  }
}
